// React Query data layer for Custom (Quality Adjustment) Templates.
//
// This is the primary, API-backed CRUD surface for templates — see useTemplates.ts for the thin,
// non-suspending wrappers built on top of these so the Template Manager / Template Editor pages
// keep their current shape unchanged.

import type { SpecType } from '@local/lib';
import type { UseMutationResult, UseSuspenseQueryResult } from '@tanstack/react-query';

import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import type {
  ICustomTemplate,
  ICustomTemplateCreate,
  ICustomTemplateListQuery,
  ICustomTemplateListResponse,
  ICustomTemplateUpdate,
  TemplateScope,
} from '../templateTypes';

import { queryKeys } from '../../../api';
import {
  createCustomTemplate as apiCreateCustomTemplate,
  deleteCustomTemplate as apiDeleteCustomTemplate,
  updateCustomTemplate as apiUpdateCustomTemplate,
  fetchCustomTemplateById,
  fetchCustomTemplates,
} from '../../../api/templatesApi';

// '*' (no scope) is omitted from cache keys/filters rather than sent as a literal filter value.
const scopeFilter = (scope: TemplateScope): SpecType | undefined => (scope === '*' ? undefined : scope);

// ============================================================================
// Query Options - Primary API (2025 Best Practice)
// ============================================================================

/**
 * Query options for fetching a single template by id.
 * @param scope - Expected Quality Specification, or `'*'` to skip that check
 * @param templateId - The template id
 */
export const customTemplateQueryOptions = (scope: TemplateScope, templateId: string) =>
  queryOptions({
    queryFn: ({ signal }) => fetchCustomTemplateById(scope, templateId, signal),
    // `scope` is appended (not baked into `detail()` itself) so `queryKeys.templates.detail(id)`
    // still works as a scope-agnostic prefix for invalidation/removal (see useDeleteCustomTemplate).
    queryKey: [...queryKeys.templates.detail(templateId), scope],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for fetching a list of templates.
 * @param scope - Quality Specification to filter by, or `'*'` for every spec
 * @param filters - Optional pagination/status filters
 * @param isGlobalSearch - Whether this is a global (cross-spec) search query (affects caching behavior)
 */
export const customTemplatesListQueryOptions = (
  scope: TemplateScope,
  filters: Partial<ICustomTemplateListQuery> = {},
  isGlobalSearch = false,
) =>
  queryOptions({
    queryFn: ({ signal }) => fetchCustomTemplates(scope, filters, signal),
    queryKey: queryKeys.templates.list({ ...filters, specType: scopeFilter(scope) }),
    // For global search, don't refetch on mount if we have cached data
    refetchOnMount: !isGlobalSearch,
    // Global search never goes stale - only refreshes on manual refresh or mutation
    // Regular queries still refresh after 2 minutes
    staleTime: isGlobalSearch ? Infinity : 1000 * 60 * 2,
  });

// ============================================================================
// Query Hooks - Convenience Wrappers
// ============================================================================

export const useCustomTemplates = (
  scope: TemplateScope,
  filters: Partial<ICustomTemplateListQuery> = {},
  isGlobalSearch = false,
): UseSuspenseQueryResult<ICustomTemplateListResponse> =>
  useSuspenseQuery(customTemplatesListQueryOptions(scope, filters, isGlobalSearch));

export const useCustomTemplate = (scope: TemplateScope, templateId: string): UseSuspenseQueryResult<ICustomTemplate> =>
  useSuspenseQuery(customTemplateQueryOptions(scope, templateId));

export const useCreateCustomTemplate = (): UseMutationResult<
  ICustomTemplate,
  Error,
  { data: ICustomTemplateCreate; scope: TemplateScope }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, scope }) => apiCreateCustomTemplate(scope, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.templates.lists(), refetchType: 'all' });
    },
  });
};

export const useUpdateCustomTemplate = (): UseMutationResult<
  ICustomTemplate,
  Error,
  { data: ICustomTemplateUpdate; scope: TemplateScope; templateId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, scope, templateId }) => apiUpdateCustomTemplate(scope, templateId, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.templates.detail(variables.templateId),
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.templates.lists(), refetchType: 'all' });
    },
  });
};

export const useDeleteCustomTemplate = (): UseMutationResult<
  void,
  Error,
  { scope: TemplateScope; templateId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scope, templateId }) => apiDeleteCustomTemplate(scope, templateId),
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: queryKeys.templates.detail(variables.templateId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.templates.lists(), refetchType: 'all' });
    },
  });
};

// ============================================================================
// Global Search Convenience Hooks
// ============================================================================

/**
 * Fetch all templates (across every Quality Specification).
 * Uses staleTime: Infinity - only refreshes on manual refresh or mutation.
 */
export const useAllCustomTemplates = (): UseSuspenseQueryResult<ICustomTemplateListResponse> =>
  useCustomTemplates('*', {}, true);
